import { Heading, Strong } from '@radix-ui/themes';
import { CommonAlert } from 'components/common/alert';
import { BadgeCurrency } from 'components/common/badges/currency';
import { BadgeTrxType } from 'components/common/badges/transaction-type';
import { CommonDialog } from 'components/common/dialog';
import { CommonFormDialog } from 'components/common/dialog/form-dialog';
import { ActionSelect } from 'components/layout/action-select';
import { EB } from 'components/layout/error-boundary';
import { TransactionDetails } from 'components/main/routes/transactions/details';
import { TransactionForm } from 'components/main/routes/transactions/form';
import { CommonTable } from 'components/table';
import { AuthContext } from 'contexts/auth';
import { DialogType } from 'enums/dialog';
import { DialogState } from 'interfaces/dialog/state';
import { BaseDoc } from 'lib/interfaces/database/_base';
import { TransactionWhole } from 'lib/interfaces/database/transaction';
import { SortUtil } from 'lib/utilities/sort';
import { StringUtil } from 'lib/utilities/string';
import { useContext, useEffect, useState } from 'react';
import useTransactionsStore from 'stores/transactions';

export const Transactions = () => {
  const { data, create, load, update, markDeleted } = useTransactionsStore();

  const { perms } = useContext(AuthContext);
  const { transactions: crud } = perms;

  useEffect(() => {
    if (crud?.read) {
      load();
    }
  }, [crud?.read, load]);

  const [dialog, setDialog] = useState<DialogState>();

  return (
    <EB>
      <Heading>Transactions</Heading>

      <CommonTable
        defaultSort={{
          iCol: 2,
          asc: false,
        }}
        toolbarActions={[
          {
            hidden: !crud?.create,
            label: 'Create',
            onClick: () =>
              setDialog({
                key: Date.now(),
                type: DialogType.Create,
                data: {},
              }),
          },
        ]}
        columns={[
          {
            header: 'Actions',
            rowFn: (item: TransactionWhole) => (
              <ActionSelect
                options={[
                  {
                    // anyone who can update a transaction doesn't need the view-only dialog and vice-versa
                    // hidden: crud?.update,
                    label: 'Details',
                    value: DialogType.Inspect,
                  },
                  {
                    hidden: !crud?.update,
                    label: 'Update',
                    value: DialogType.Update,
                  },
                  {
                    hidden: !crud?.delete,
                    label: 'Delete',
                    value: DialogType.Delete,
                  },
                ]}
                onValueChange={(v) => {
                  // new value never gets set but it does trigger the effect below
                  switch (v) {
                    case DialogType.Inspect:
                    case DialogType.Update:
                    case DialogType.Delete: {
                      setDialog({
                        key: Date.now(),
                        type: v,
                        data: item,
                      });
                      break;
                    }

                    default: {
                      break;
                    }
                  }
                }}
              />
            ),
          },
          {
            header: 'Created',
            rowFn: (item: TransactionWhole) =>
              StringUtil.printDateTime(item.createdAt),
            sortFn: (a: TransactionWhole, b: TransactionWhole) =>
              SortUtil.dates(a.createdAt, b.createdAt),
          },
          {
            header: 'Trx Date',
            rowFn: (item: TransactionWhole) =>
              StringUtil.printDate(item.transDate),
            sortFn: (a: TransactionWhole, b: TransactionWhole) =>
              SortUtil.dates(a.transDate, b.transDate),
          },
          {
            header: 'Settle Date',
            rowFn: (item: TransactionWhole) =>
              StringUtil.printDate(item.settleDate),
            sortFn: (a: TransactionWhole, b: TransactionWhole) =>
              SortUtil.dates(a.settleDate, b.settleDate),
          },
          {
            header: 'Conf. Date',
            rowFn: (item: TransactionWhole) =>
              StringUtil.printDate(item.confirmDate),
            sortFn: (a: TransactionWhole, b: TransactionWhole) =>
              SortUtil.dates(a.confirmDate, b.confirmDate),
          },
          {
            header: 'Investor',
            rowFn: (item: TransactionWhole) => item._investor?.code,
            sortFn: (a: TransactionWhole, b: TransactionWhole) =>
              SortUtil.strings(a._investor?.code, b._investor?.code),
          },
          {
            header: 'Account',
            rowFn: (item: TransactionWhole) => item._account?.shortIdentifier,
            sortFn: (a: TransactionWhole, b: TransactionWhole) =>
              SortUtil.strings(
                a._account?.shortIdentifier,
                b._account?.shortIdentifier
              ),
          },
          {
            header: 'Security',
            rowFn: (item: TransactionWhole) => item._security?.name,
            sortFn: (a: TransactionWhole, b: TransactionWhole) =>
              SortUtil.strings(a._security?.name, b._security?.name),
          },
          {
            header: 'Type',
            rowFn: (item: TransactionWhole) => (
              <BadgeTrxType value={item.type} />
            ),
            sortFn: (a: TransactionWhole, b: TransactionWhole) =>
              SortUtil.strings(a.type, b.type),
          },
          {
            header: 'Div Type',
            rowFn: (item: TransactionWhole) => item.dividendType,
            sortFn: (a: TransactionWhole, b: TransactionWhole) =>
              SortUtil.strings(a.dividendType, b.dividendType),
          },
          {
            header: 'Amount',
            rowFn: (item: TransactionWhole) =>
              StringUtil.printMoney(item.amount),
            sortFn: (a: TransactionWhole, b: TransactionWhole) =>
              SortUtil.numbers(a.amount, b.amount),
          },
          {
            header: 'Currency',
            rowFn: (item: TransactionWhole) => (
              <BadgeCurrency value={item.currency} />
            ),
            sortFn: (a: TransactionWhole, b: TransactionWhole) =>
              SortUtil.strings(a.currency, b.currency),
          },
          {
            header: 'Units',
            rowFn: (item: TransactionWhole) =>
              StringUtil.printUnits(item.units),
            sortFn: (a: TransactionWhole, b: TransactionWhole) =>
              SortUtil.numbers(a.units, b.units),
          },
        ]}
        data={data}
      />

      {dialog?.type === DialogType.Create && (
        <CommonFormDialog
          key={dialog.key}
          title="Create"
          children={<TransactionForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => create(item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.type === DialogType.Inspect && (
        <CommonDialog
          key={dialog.key}
          title="Transaction Details"
          children={<TransactionDetails trx={dialog.data} />}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.type === DialogType.Update && (
        <CommonFormDialog
          key={dialog.key}
          title="Update"
          children={<TransactionForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => update((dialog.data as BaseDoc).id, item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.type === DialogType.Delete && (
        <CommonAlert
          key={dialog.key}
          title="Delete"
          description={
            <>
              Are you sure you want to mark this transaction as deleted?
              <br />
              <Strong>
                This cannot be undone without contacting an administrator.
              </Strong>
            </>
          }
          onConfirm={() => markDeleted((dialog.data as BaseDoc).id)}
        />
      )}
    </EB>
  );
};
